import { DiagnosticType, IntervalEndingEnum } from '@/services/models/enums'

export type Variable = {
  id: string
  name: string
  parameterizationType: ParameterizationType
  type: {
    id: string | null
    name: string
    color: string
    diagnosticType: DiagnosticType
    upstreamEffectsDisabled: boolean
    isPublishable: boolean
    useWorstStateAsReference: boolean
  }
  comment: string
  tags?: string[] | null
  definition?: string | null
  references?: string | null
  formula?: string | null
  prevalenceExplanation?: string | null
  stateIds: string[]
  inboundArrows: Connection[]
  outboundArrows: Connection[]
  probabilities: number[]
  faults?: string[]
  intervals?: Interval[]
  intervalUnit?: string
  optimalStateId: string | null
  copyReferenceId: string
  basePopulationVariableId: string | null
  basePopulationStateId: string | null
  approvedDistinguishedStates: Record<string, string[]>
  updatedAt: string
  // KBE-653: continuous state-transition functions (KBE-612). One config per
  // adjacent state pair, aligned to `intervals` (length = stateIds.length - 1
  // when set). NULL/undefined for discrete and semi-continuous variables. The
  // transition midpoint IS stored here since KBE-858, on every stored entry; only
  // an INHERITING variable (a NULL column) derives it from the shared boundary of
  // the two adjacent intervals (see src/utils/network/transitionFunctions.ts).
  // Optional like `intervals` --
  // the DB column is NULL for discrete/semi-continuous variables, and
  // convertVariables normalizes the read side to `... ?? null`.
  transitionFunctions?: TransitionFunction[] | null
}

export type VariableFromBackend = Omit<Variable, 'type'> & {
  typeId: string | null
}

export type Connection = {
  variableId: string
  comment: string
}

export type Arrow = {
  source: string
  target: string
  comment: string
}

export enum IntervalSide {
  lower = 'lower',
  upper = 'upper',
}

export type Interval = {
  lowerBound: number
  upperBound: number
  lowerEnding: IntervalEndingEnum
  upperEnding: IntervalEndingEnum
}

export enum ParameterizationType {
  NoisyMax = 'noisyMax',
  General = 'general',
}

// One transition between an adjacent pair of states: how far either side of the
// boundary crossing it becomes 5% and 95% likely, in the variable's own units.
// The curve fitted through those two points and the boundary is a shifted
// log-logistic; the boundary itself is the 50% point and is NOT stored here (it
// is derived from the shared bound of the two adjacent intervals, so `intervals`
// stays the single source of truth for where each transition sits).
//
// `ninetyFivePercentValue` absent (or null) is the SYMMETRICAL mode: the 95%
// point sits the same distance above the MIDPOINT as the 5% point sits below it,
// the asymmetry ratio is exactly 1, and the curve is the plain logistic --
// bit-for-bit the sigmoid KBE-653 shipped. Set, it is the SKEWED mode and the
// curve is genuinely asymmetric. Absence rather than a separate discriminant
// field, so a stored entry cannot contradict itself (a "symmetrical" row carrying
// a 95% value).
//
// Symmetrical/skewed is KBE's own AUTHORING vocabulary and goes no further: since
// bayesian-utils 0.2.0 the library knows only `ResolvedTransition`, all three
// points required, so KBE resolves the mirror before it calls the curve or writes
// an export (KBE-858).
//
// KBE-858 stores ABSOLUTE values again, reversing KBE-817's distances -- but the
// reason the distances existed is gone rather than overruled. A distance made a
// config travel when its BOUNDARY moved underneath it, because the boundary was
// the midpoint and lived in another column. The midpoint is stored here now, so it
// moves when a researcher types it and not otherwise, and there is nothing left
// for a boundary move to strand: the entry states its own curve completely.
//
// The deciding case is the ordinary authoring order. A researcher reads "5% at
// 5.4" off a guideline table and enters it, then sets the crossover. 5.4 has to
// stay 5.4 -- under distance storage, moving the midpoint translated it sideways.
//
// Two things fall out, both deletions. There is no absolute<->distance conversion
// anywhere any more (`transitionFunctionFromValues` is gone), so the value typed
// is the value stored is the value redisplayed. And a HARD CUTOFF (KBE-775) is
// three coinciding values rather than a distance that must stay exactly zero
// through every edit -- no subtraction, so no float dust to guard against.
//
// The accepted cost is the one KBE-817 bought out and this buys back knowingly:
// moving an interval bound no longer moves a tuned variable's transition. It is
// silent at the point of edit, and the diff surfaces (`storedTransitionsByBoundary`
// and everything keyed off it) are the mitigation -- they compare stored midpoints,
// so a bound moving away from one is reported.
//
// The invariant is entry-internal and nothing relates it to `intervals`:
// `fivePercentValue <= midpoint <= ninetyFivePercentValue`, enforced by the DB
// CHECK and by the library's exported `isValidTransition` on the write path.
export type TransitionFunction = {
  midpoint: number
  fivePercentValue: number
  ninetyFivePercentValue?: number | null
}

// A transition with its boundary attached and both values present -- what
// `resolveTransitionFunctions` produces and every consumer downstream reads. The
// mirroring a Symmetrical entry implies, and the network default an inheriting
// variable follows, are both already applied, so nothing downstream needs to
// know which mode or which source produced these numbers.
export type ResolvedTransition = {
  midpoint: number
  fivePercentValue: number
  ninetyFivePercentValue: number
}

// KBE-769: the fallback used only when no network-level default is in reach --
// Storybook stories and unit tests that have no network row. Every real read
// path resolves the default from `network.default_transition_half_band_percent`,
// which is NOT NULL.
//
// This lives here rather than in utils/network/transitionFunctions.ts because
// networkTable.ts needs it for the column default and must not import from
// utils (which reaches back into services/db). V125 hardcodes the same value --
// SQL cannot read a TS constant, so changing it means changing both plus a
// migration for the existing rows.
export const DEFAULT_TRANSITION_HALF_BAND_PERCENT = 5

// The per-network default a variable inherits while its own
// `transition_functions` is NULL (KBE-769). It stays RELATIVE even though the
// per-variable override is absolute (KBE-772): one percentage is
// scale-appropriate across every variable in the network, where one absolute
// value could only ever be right at one scale. Named for the column it is read
// from so call sites reading a network row can pass it straight through.
export type NetworkTransitionDefaults = {
  defaultTransitionHalfBandPercent: number
}

// Discrete (named states) vs. semi-continuous (interval-valued). Lives
// in this shared types file -- not in a Client Component file -- so
// server-side callers (the assistant's `compareNetworks` tool ->
// `buildNetworkComparison` -> `pairEntitiesInTwoNetwork`) can import it
// without pulling client-only code into the App Router server bundle.
export enum Granularity {
  Discrete = 'discrete',
  SemiContinuous = 'semi-continuous',
}
